import cn from 'classnames';
import { useCallback, useContext } from 'react';

import { UserContext, VoucherContext, BasketContext } from 'components/signUpFunnel/Contexts';
import InternalSkipButton from 'components/signUpFunnel/InternalSkip/InternalSkipButton.js';
import { TEMPO } from 'context/BrandContext';
import {
  createCheckoutWithDeliveryDateRetry as createCheckoutWithDeliveryDateRetryRequest,
  getFirstAvailableDeliveryDate as getFirstAvailableDeliveryDateRequest,
  getPickMealsRecommendation as getPickMealsRecommendationRequest,
} from 'helpers/internalSkipFlow';
import {
  buildPaymentParams,
  generateRandomName,
  generateChicagoStreetAddress,
  generateChicagoPhoneNumber,
  getFunnelPages,
  PICK_MEALS_INTRO_PAGES,
  parseNameFromEmail,
} from 'helpers/internalSkipFunctions';
import { OUR_MENU_PATH } from 'helpers/routes';
import useInternalSkip, { NAVIGATION_MODE } from 'hooks/useInternalSkip';
import { FAMILY, STANDARD, STANDARD_MEAL_SERVING_SIZE } from 'lib/constants';
import { Statuses, SKIP_PATHS } from 'lib/constants/signUp';
import * as ErrorLogging from 'services/ErrorLogging';
import RequestService from 'services/RequestService';

const SkipContainer = ({
  introSubmitParams,
  setError = () => {},
  setIsSubmitting = () => {},
  mealPlan,
  isPickMeals = false,
  renderInSlideOut = false,
  onSlideOutNavigate,
  dismissSlideOut,
  showSkipTargetsOnCurrentStep,
}) => {
  const shouldHideInternalSkip = RUNTIME_ENV === 'production'; /* global RUNTIME_ENV */

  const { internalValues, handlePath, setIsInternalVisible, isInternalVisible } = useInternalSkip(
    mealPlan,
    { onSlideOutNavigate }
  );
  const { user, setUser } = useContext(UserContext);
  const { setVoucher } = useContext(VoucherContext);
  const { setBasket } = useContext(BasketContext);

  const isTempo = mealPlan === TEMPO;
  const isHomeChef = mealPlan === STANDARD;
  const isFamilyPlan = mealPlan === FAMILY;

  const funnelPages = getFunnelPages(isFamilyPlan);
  const pickMealsIntroPages = PICK_MEALS_INTRO_PAGES;

  const updateUser = useCallback(
    async user => {
      const userResponse = await RequestService.updateUser({
        params: {
          name: user?.name || parseNameFromEmail(user) || generateRandomName('full'),
          fullName: user?.fullName || parseNameFromEmail(user) || generateRandomName('full'),
          address: {
            streetAddress: user?.streetAddress || generateChicagoStreetAddress(),
          },
          phone: user?.phone || generateChicagoPhoneNumber(),
        },
      });

      if (userResponse?.ok) {
        const updatedUser = {
          ...userResponse.user,
          ...user,
          address: {
            ...userResponse.user.address,
            ...user.address,
          },
        };

        setUser(updatedUser);
        return updatedUser;
      }

      return user;
    },
    [setUser]
  );

  const createInternalUser = useCallback(async () => {
    if (user?.id && user?.email && user?.signupZip) {
      return updateUser(user);
    }

    const values = internalValues();
    const params = introSubmitParams
      ? introSubmitParams(values)
      : {
          mealPlanSlug: values.mealPlanSlug,
          email: values.introEmail,
          signupZip: values.introZipCode,
          mealKitTraits: [],
        };
    const submitFn = user?.id ? RequestService.updateFunnelUser : RequestService.createUser;
    const response = await submitFn({ params });

    if (!response?.ok) {
      setError(response?.error);
      return null;
    }

    const createdUser = response.user;
    setVoucher(createdUser.signupVoucher);

    return updateUser(createdUser);
  }, [internalValues, introSubmitParams, setError, setVoucher, updateUser, user]);

  const getPickMealsRecommendation = useCallback(() => getPickMealsRecommendationRequest(), []);

  const getFirstAvailableDeliveryDate = useCallback(
    args => getFirstAvailableDeliveryDateRequest({ ...args, setError }),
    [setError]
  );

  const createCheckoutWithDeliveryDateRetry = useCallback(
    args => createCheckoutWithDeliveryDateRetryRequest({ ...args, setError }),
    [setError]
  );

  const handleBoxOrBillingPath = useCallback(
    async user => {
      let servings = isTempo ? 8 : 2;
      let weeklyMeals = isTempo ? 6 : user.weeklyMeals;

      if (isTempo) {
        await RequestService.updateBoxSignupAnswerSelections({
          params: { box_recommendation: { answerIds: [8] } },
        });
      }

      const response = await RequestService.updateBox({
        params: { servings, weeklyMeals },
      });

      if (response?.ok) {
        const billingPageUser = response.user;

        setUser(billingPageUser);
        setBasket({
          servings: billingPageUser.servings,
          meals: billingPageUser.weeklyMeals,
        });
        return billingPageUser;
      }

      return null;
    },
    [isTempo, setBasket, setUser]
  );

  const handleThankYouPath = useCallback(
    async user => {
      if (shouldHideInternalSkip) {
        throw new Error('Internal skip tool cannot be used in production');
      }

      const billingPageUser = await handleBoxOrBillingPath(user);

      if (!billingPageUser) return false;

      const firstDeliveryDate = await getFirstAvailableDeliveryDate({
        zipCode: billingPageUser?.signupZip || user?.signupZip,
      });

      if (!firstDeliveryDate) return false;

      const paymentParams = buildPaymentParams(billingPageUser, user, firstDeliveryDate);

      const response = await createCheckoutWithDeliveryDateRetry({
        paymentParams,
        deliveryOptionsParams: {
          zipCode: billingPageUser?.signupZip || user?.signupZip,
        },
      });

      if (response?.ok) {
        setBasket(response.weeklyBasket);
        setUser({ ...billingPageUser, ...response.user, accessToken: response.accessToken });
        return true;
      }

      setError(response?.error);
      return false;
    },
    [
      shouldHideInternalSkip,
      createCheckoutWithDeliveryDateRetry,
      getFirstAvailableDeliveryDate,
      handleBoxOrBillingPath,
      setBasket,
      setError,
      setUser,
    ]
  );

  const handlePickMealsPath = useCallback(
    async user => {
      const { servings, weeklyMeals } = await getPickMealsRecommendation();
      const response = await RequestService.updateBox({ params: { servings, weeklyMeals } });

      if (!response?.ok) {
        setError(response?.error);
        return false;
      }

      const pickMealsUser = { ...user, ...response.user };
      setUser(pickMealsUser);
      setBasket({
        servings: pickMealsUser.servings,
        meals: pickMealsUser.weeklyMeals,
      });

      const statusResponse = await RequestService.updateStatus({
        params: { status: Statuses.PICK_MEALS_STEP },
      });

      if (statusResponse?.ok) {
        setUser(prevUser => ({ ...prevUser, status: Statuses.PICK_MEALS_STEP }));
      }

      const navigationMode = handlePath(SKIP_PATHS.PICK_MEALS);
      if (navigationMode === NAVIGATION_MODE.HISTORY) {
        dismissSlideOut?.();
      }
      return true;
    },
    [dismissSlideOut, getPickMealsRecommendation, handlePath, setBasket, setError, setUser]
  );

  const buildPickMealsBasket = useCallback(
    async existingUser => {
      const { weeklyMeals } = await getPickMealsRecommendation();
      const menuResponse = await RequestService.getPickMealsMenu();

      if (!menuResponse?.ok) {
        setError(menuResponse?.error);
        return null;
      }

      const defaultMeals = menuResponse.meals
        .slice(0, weeklyMeals)
        .map(meal => ({ id: meal.id, servings: STANDARD_MEAL_SERVING_SIZE }));

      const basketResponse = await RequestService.createPickMealsBasket({
        params: {
          menuId: menuResponse.id,
          meals: defaultMeals,
        },
      });

      if (!basketResponse?.ok) {
        setError(basketResponse?.error);
        return null;
      }

      const mergedUser = { ...existingUser, ...basketResponse.user };

      setUser(prevUser => ({ ...prevUser, ...mergedUser }));
      return mergedUser;
    },
    [getPickMealsRecommendation, setError, setUser]
  );

  const handlePickMealsBillingPath = useCallback(
    async user => {
      const billingUser = await buildPickMealsBasket(user);

      if (!billingUser) return false;

      const navigationMode = handlePath(SKIP_PATHS.PICK_MEALS_BILLING);
      if (navigationMode === NAVIGATION_MODE.HISTORY) {
        dismissSlideOut?.();
      }
      return true;
    },
    [buildPickMealsBasket, dismissSlideOut, handlePath]
  );

  const handlePickMealsThankYouPath = useCallback(
    async user => {
      if (shouldHideInternalSkip) {
        throw new Error('Internal skip tool cannot be used in production');
      }

      const thankYouUser = await buildPickMealsBasket(user);

      if (!thankYouUser) return false;

      const firstDeliveryDate = await getFirstAvailableDeliveryDate({
        zipCode: thankYouUser?.signupZip || user?.signupZip,
        pickYourMeals: true,
      });

      if (!firstDeliveryDate) return false;

      const paymentParams = {
        ...buildPaymentParams(thankYouUser, user, firstDeliveryDate),
        servings: thankYouUser?.servings || STANDARD_MEAL_SERVING_SIZE,
      };

      const response = await createCheckoutWithDeliveryDateRetry({
        paymentParams,
        deliveryOptionsParams: {
          zipCode: thankYouUser?.signupZip || user?.signupZip,
          pickYourMeals: true,
        },
      });

      if (!response?.ok) {
        setError(response?.error);
        return false;
      }

      setBasket(response.weeklyBasket);
      setUser({ ...thankYouUser, ...response.user, accessToken: response.accessToken });

      const navigationMode = handlePath(SKIP_PATHS.PICK_MEALS_THANK_YOU);
      if (navigationMode === NAVIGATION_MODE.HISTORY) {
        dismissSlideOut?.();
      }
      return true;
    },
    [
      shouldHideInternalSkip,
      buildPickMealsBasket,
      createCheckoutWithDeliveryDateRetry,
      dismissSlideOut,
      getFirstAvailableDeliveryDate,
      handlePath,
      setBasket,
      setError,
      setUser,
    ]
  );

  const handleSubmit = useCallback(
    async (to, status) => {
      setError('');
      setIsSubmitting(true);

      try {
        const userWithName = await createInternalUser();

        if (!userWithName) {
          return;
        }

        if (isPickMeals) {
          if (to === SKIP_PATHS.PREF_DIETARY) {
            handlePath(to);
            return;
          }

          if (to === SKIP_PATHS.PICK_MEALS) {
            await handlePickMealsPath(userWithName);
            return;
          }

          if (to === SKIP_PATHS.PICK_MEALS_BILLING) {
            await handlePickMealsBillingPath(userWithName);
            return;
          }

          if (to === SKIP_PATHS.PICK_MEALS_THANK_YOU) {
            await handlePickMealsThankYouPath(userWithName);
            return;
          }
        }

        if (to === SKIP_PATHS.THANK_YOU) {
          const success = await handleThankYouPath(userWithName);
          if (!success) return;
        } else if (to === SKIP_PATHS.BILLING || to === SKIP_PATHS.BOX) {
          const nextUser = await handleBoxOrBillingPath(userWithName);
          if (to === SKIP_PATHS.BILLING && !nextUser) return;

          await RequestService.updateStatus({
            params: { status },
          });

          setUser(prevUser => ({
            ...prevUser,
            ...(nextUser || userWithName),
            status,
          }));
        } else {
          const statusResponse = await RequestService.updateStatus({
            params: { status },
          });

          if (statusResponse?.ok) {
            setUser({
              ...userWithName,
              status,
            });
          }

          setUser(prevUser => ({
            ...prevUser,
            ...userWithName,
            status,
          }));
        }

        handlePath(to);
      } catch (error) {
        setError(error.message);
        ErrorLogging.captureException(error.message);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      createInternalUser,
      handleBoxOrBillingPath,
      handlePath,
      handlePickMealsBillingPath,
      handlePickMealsPath,
      handlePickMealsThankYouPath,
      handleThankYouPath,
      isPickMeals,
      setError,
      setIsSubmitting,
      setUser,
    ]
  );

  const skipTargets = isPickMeals ? pickMealsIntroPages : funnelPages;

  const handleLogoutAndRedirect = useCallback(
    async path => {
      try {
        const response = await RequestService.logoutUser();
        if (response?.ok) {
          if (path) {
            window.location.replace(path);
          } else {
            window.location.reload();
            handlePath(SKIP_PATHS.INTRO);
          }
        }
      } catch (error) {
        ErrorLogging.captureException(error.message);
      }
    },
    [handlePath]
  );

  const handleSwitchBrands = useCallback(
    async (requestFunction, path = null) => {
      try {
        const response = await requestFunction();
        if (response?.ok) {
          handleLogoutAndRedirect(path);
        }
      } catch {
        // no op
      }
    },
    [handleLogoutAndRedirect]
  );

  const handleTempoLogout = useCallback(
    path => {
      if (isTempo) {
        return handleLogoutAndRedirect(path);
      }

      return handleSwitchBrands(RequestService.enterTempo, path);
    },
    [handleLogoutAndRedirect, handleSwitchBrands, isTempo]
  );

  const logOutToBrand = [
    {
      page: 'Home Chef',
      onSubmit: () => handleSwitchBrands(RequestService.exitTempo, '/signup/intro/standard'),
    },
    {
      page: 'Tempo - PYM',
      onSubmit: () => handleTempoLogout(`${OUR_MENU_PATH}/tempo?pym=true`),
    },
    {
      page: 'Tempo - Funnel',
      onSubmit: () => handleTempoLogout('/signup/intro/tempo?skip_pick_meals_redirect=true'),
    },
    {
      page: 'Family',
      onSubmit: () =>
        handleSwitchBrands(RequestService.exitTempo, '/signup/intro/family?family=true'),
    },
  ];

  const showSkipTargets = (showSkipTargetsOnCurrentStep ?? isPickMeals) || !user?.email;
  const panelContent = showSkipTargets ? (
    <div className="p-4">
      {skipTargets.map(({ page, path, status }) => (
        <InternalSkipButton
          key={page}
          funnelPage={page}
          onSubmit={() => handleSubmit(path, status)}
        />
      ))}
    </div>
  ) : (
    <div className="p-4">
      <p className="mb-1 text-center text-xs">Logged in as</p>
      <p className="mb-2 text-center text-xxs">{user.email}</p>
      {logOutToBrand.map(({ page, onSubmit }) => (
        <InternalSkipButton key={page} funnelPage={page} onSubmit={onSubmit} logout={true} />
      ))}
    </div>
  );

  if (shouldHideInternalSkip) return null;

  if (renderInSlideOut) {
    return (
      <div className="mt-4 h-10">
        <div className="absolute left-4 top-4 z-10 text-left">
          <div className="w-52 rounded-xl bg-white-100 shadow-md">
            <button
              type="button"
              className={cn(
                'w-full cursor-pointer px-4 py-2 text-center text-sm font-medium focus:outline-none',
                {
                  'rounded-t-lg border-b': isInternalVisible,
                  'rounded-xl': !isInternalVisible,
                  'hover:bg-tempo-blue-70': isTempo,
                  'hover:bg-lemon-70': isFamilyPlan,
                  'hover:bg-green-50': isHomeChef,
                }
              )}
              onClick={() => setIsInternalVisible(!isInternalVisible)}
            >
              {isInternalVisible ? 'Hide Skip Feature' : 'Show Skip Feature'}
            </button>
            {isInternalVisible && panelContent}
          </div>
        </div>
      </div>
    );
  }

  if (isPickMeals) {
    return (
      <div className="fixed left-3 top-3 z-50 w-52 rounded-xl bg-white-100 shadow-md">
        <button
          type="button"
          className={cn(
            'w-full cursor-pointer px-4 py-2 text-center text-sm font-medium focus:outline-none',
            {
              'rounded-t-lg border-b': isInternalVisible,
              'rounded-xl': !isInternalVisible,
              'hover:bg-tempo-blue-70': isTempo,
              'hover:bg-lemon-70': isFamilyPlan,
              'hover:bg-green-50': isHomeChef,
            }
          )}
          onClick={() => setIsInternalVisible(!isInternalVisible)}
        >
          {isInternalVisible ? 'Hide Skip Feature' : 'Show Skip Feature'}
        </button>
        {isInternalVisible && panelContent}
      </div>
    );
  }

  return (
    <div className="relative">
      <div
        className={cn('fixed left-3 top-32 z-50 w-52 rounded-xl bg-white-100 shadow-md bp1:top-14')}
      >
        <button
          type="button"
          className={cn(
            'w-full cursor-pointer px-4 py-2 text-center text-sm font-medium focus:outline-none',
            {
              'rounded-t-lg border-b': isInternalVisible,
              'rounded-xl': !isInternalVisible,
              'hover:bg-tempo-blue-70': isTempo,
              'hover:bg-lemon-70': isFamilyPlan,
              'hover:bg-green-50': isHomeChef,
            }
          )}
          onClick={() => setIsInternalVisible(!isInternalVisible)}
        >
          {isInternalVisible ? 'Hide Skip Feature' : 'Show Skip Feature'}
        </button>
        {isInternalVisible && panelContent}
      </div>
    </div>
  );
};

SkipContainer.propTypes = {
  dismissSlideOut: PropTypes.func,
  isPickMeals: PropTypes.bool,
  introSubmitParams: PropTypes.func,
  onSlideOutNavigate: PropTypes.func,
  renderInSlideOut: PropTypes.bool,
  showSkipTargetsOnCurrentStep: PropTypes.bool,
  setError: PropTypes.func,
  setIsSubmitting: PropTypes.func,
  mealPlan: PropTypes.string.isRequired,
};

export default SkipContainer;
